import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useReducer, useMemo } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { getStroke } from 'perfect-freehand';
import toast from 'react-hot-toast';

// Stage A of the layered whiteboard rebuild — see
// solomon/docs/whiteboard-architecture-plan.md.
//
// Document layer: the ORIGINAL PDF is rendered client-side with pdf.js (no
// server rasterization). Annotation layer: a transparent overlay per page whose
// strokes are stored as vectors in NORMALIZED per-page coordinates (0..1), so
// they are resolution-independent and stable across devices/zoom. Strokes sync
// as individual delta ops (stroke:add / stroke:remove / clear), not whole-scene
// resends.
//
// Stage A scope: a single PDF per classroom (no tabs yet), tutor-only editing.
// Tools: pen, eraser, pan + zoom, per-tutor undo/redo, clear. Tabs/multi-doc
// (Stage B) and annotated-PDF export (Stage C) come later.

// The pdf.js worker is copied verbatim into public/ at build time by
// scripts/copy-pdf-worker.js and served as a plain static asset. We deliberately
// DON'T use `new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url)`:
// CRA/webpack re-bundles and code-splits the worker, and in production those
// chunks resolve to Vercel's SPA fallback (index.html, text/html), which the
// browser rejects for the wrong MIME type ("Setting up fake worker failed").
// The copy comes from the installed pdfjs-dist, so worker/api versions stay in
// lockstep. PUBLIC_URL is '' at the app root, so this is just /pdf.worker.min.mjs.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

// API base for raw fetch() calls (mirrors Whiteboard.js). Empty in dev so
// relative paths fall through the CRA proxy; the Railway URL in production.
const API_BASE = process.env.REACT_APP_API_URL || '';
const apiUrl = (p) => (p.startsWith('http') ? p : `${API_BASE}${p}`);

// Base CSS render width for the PDF backdrop (at zoom 1). The page is
// re-rasterized whenever zoom changes so both PDF and ink stay crisp.
const PAGE_RENDER_WIDTH = 1000;
const BLANK_ASPECT = 1.414; // blank board page shape (A4 portrait height/width)
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;

// iOS Safari renders a canvas BLANK once it exceeds its maximum size, so at high
// zoom the annotation/PDF canvases were disappearing. Cap the backing store to
// these limits; past the cap the page renders at reduced density — slightly
// softer ink instead of vanishing.
//
// These are tuned above the conservative universal ceiling (4096 / 16.7M px²) to
// keep ink crisp on modern iPads: on-device, ~227% zoom (≈4540×6420, ~29M px²)
// was the failure point and 200% (~22.6M) rendered fine, so we cap just under
// that. Trade-off: a much older device with a 16.7M limit could blank a bit
// past ~2x — lower these back toward 4096 / 16.7M if that ever shows up.
const MAX_CANVAS_DIM = 6000;
const MAX_CANVAS_AREA = 24000000;

// Drawable margin ("mat") around every page, as a fraction of the page's own
// width/height. This lets the tutor draw in the gutters/sides — useful when a
// page is dense with text (like Teach Me 2). Because it's a fixed fraction,
// margin strokes get well-defined normalized coords (x<0 or x>1, y<0 or y>1)
// that render identically on every client. A wider MARGIN_X gives more room to
// work in the left/right gutters: at 0.5 each gutter is half the page width, so
// the page sits centred with big drawable margins filling most of the panel.
const MARGIN_X = 0.5;
const MARGIN_Y = 0.08;

// Pen palette — simple, high-contrast colours. First entry is the default.
const PEN_COLORS = ['#111827', '#DC2626', '#243a7a', '#16A34A', '#EA580C', '#7C3AED'];
// Highlighter palette — soft, translucent-friendly colours.
const HI_COLORS = ['#FDE047', '#86EFAC', '#F9A8D4', '#93C5FD', '#FDBA74'];

const genId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(z * 100) / 100));

// Tool cursors — a pen nib and an eraser block, as inline SVG data URIs with a
// meaningful hotspot so the tip/corner sits where the ink lands.
const svgCursor = (svg, x, y) =>
  `url("data:image/svg+xml;base64,${btoa(svg)}") ${x} ${y}, crosshair`;
const CURSOR_PEN = svgCursor(
  "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>" +
    "<path d='M3 21l3.5-1L18 9l-2.5-2.5L4 18l-1 3z' fill='rgb(17,24,39)' stroke='white' stroke-width='1'/>" +
    "<path d='M16 6l2.5 2.5 1.6-1.6a1.8 1.8 0 0 0-2.5-2.5L16 6z' fill='rgb(244,199,15)' stroke='white' stroke-width='1'/>" +
    '</svg>',
  2,
  22
);
const CURSOR_HI = svgCursor(
  "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>" +
    "<path d='M4 20l2-5L16 5l3 3L9 18l-5 2z' fill='rgb(253,224,71)' stroke='rgb(17,24,39)' stroke-width='1'/>" +
    "<path d='M4 20l2-5 3 3-5 2z' fill='rgb(17,24,39)'/>" +
    '</svg>',
  3,
  21
);

// perfect-freehand tuning — the same engine Excalidraw uses. These values give
// the smooth, rounded "sharpie" feel: streamline lags the input to iron out
// jitter, a little thinning adds a natural velocity taper, rounded caps/joins.
const FREEHAND_OPTS = {
  thinning: 0.55,
  smoothing: 0.62,
  streamline: 0.55,
  simulatePressure: true,
  easing: (t) => Math.sin((t * Math.PI) / 2), // ease-out — softer line ends
  start: { cap: true, taper: 0 },
  end: { cap: true, taper: 0 },
};

// Highlighter tuning — a flat, even-weight chisel with no pressure taper, so it
// lays down a consistent translucent band like a real highlighter (vs the pen's
// velocity-tapered nib).
const HIGHLIGHT_OPTS = {
  thinning: 0,
  smoothing: 0.5,
  streamline: 0.5,
  simulatePressure: false,
  start: { cap: true, taper: 0 },
  end: { cap: true, taper: 0 },
};
const HIGHLIGHT_ALPHA = 0.4;

// Turn perfect-freehand's outline points into a smooth, closed fill path. Corners
// are rounded with quadratic curves through their midpoints (per the pf docs).
function outlineToPath(outline) {
  if (!outline.length) return null;
  const d = outline.reduce(
    (acc, [x0, y0], i, arr) => {
      const [x1, y1] = arr[(i + 1) % arr.length];
      acc.push(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
      return acc;
    },
    ['M', ...outline[0], 'Q']
  );
  d.push('Z');
  return new Path2D(d.join(' '));
}

// `map` translates normalized page coords into device pixels on the overlay
// (which is larger than the page by the margin): { PW, PH, offX, offY }, where
// the page occupies [offX, offX+PW] x [offY, offY+PH]. Normalized 0..1 maps onto
// the page; negative / >1 values land in the surrounding drawable margin.
const toPx = (map, nx, ny) => [map.offX + nx * map.PW, map.offY + ny * map.PH];

// Draw one stroke onto an overlay context as a filled, rounded outline.
function drawStroke(ctx, stroke, map) {
  const pts = stroke.points || [];
  if (pts.length === 0) return;
  const isHi = stroke.tool === 'highlighter';
  const pixelPts = pts.map(([x, y]) => toPx(map, x, y));
  // Weight multiplier: pen ~2.6x reads like a marker (not a hairline); the
  // highlighter is much broader. Scale by page width so weight stays consistent
  // across zoom levels.
  const size = (stroke.width || 3) * (isHi ? 6 : 2.6) * (map.PW / PAGE_RENDER_WIDTH);
  const outline = getStroke(pixelPts, { ...(isHi ? HIGHLIGHT_OPTS : FREEHAND_OPTS), size });
  const path = outlineToPath(outline);
  if (!path) return;
  ctx.save();
  if (isHi) ctx.globalAlpha = HIGHLIGHT_ALPHA; // translucent so PDF text reads through
  ctx.fillStyle = stroke.color || '#111827';
  ctx.fill(path);
  ctx.restore();
}

// Eraser hit-test / indicator radius in overlay device pixels. Scaling by
// (PW / PAGE_RENDER_WIDTH) keeps the eraser the same visual size across zoom
// levels and device pixel ratios, matching how pen weight is scaled.
const eraseRadiusPx = (map, eraserSize) =>
  eraserSize * (map ? map.PW / PAGE_RENDER_WIDTH : 1);

// True if a pointer at device-pixel (px,py) is within `radius` of any point of
// the stroke. Coalesced sampling keeps points dense enough that point-hit
// testing is good enough for an eraser (no need for full segment distance).
function strokeHit(stroke, px, py, map, radius) {
  const r2 = radius * radius;
  for (const [nx, ny] of stroke.points || []) {
    const [sx, sy] = toPx(map, nx, ny);
    const dx = sx - px;
    const dy = sy - py;
    if (dx * dx + dy * dy <= r2) return true;
  }
  return false;
}

// Partial erase: drop the points within `radius` of (px,py) and return the
// surviving runs as new sub-strokes (same colour/width/tool, fresh ids). Returns
// null when the eraser didn't touch this stroke (so the caller can skip it), or
// [] when the whole stroke fell inside the eraser (i.e. delete it entirely).
// Runs shorter than 2 points are dropped as visually negligible specks.
function splitStrokeByErase(stroke, px, py, map, radius) {
  const pts = stroke.points || [];
  const r2 = radius * radius;
  let touched = false;
  const runs = [];
  let run = [];
  for (const p of pts) {
    const [sx, sy] = toPx(map, p[0], p[1]);
    const dx = sx - px;
    const dy = sy - py;
    if (dx * dx + dy * dy <= r2) {
      touched = true;
      if (run.length) { runs.push(run); run = []; }
    } else {
      run.push(p);
    }
  }
  if (run.length) runs.push(run);
  if (!touched) return null;
  return runs
    .filter((r) => r.length >= 2)
    .map((r) => ({ ...stroke, id: genId(), points: r }));
}

// A single PDF page: the rasterized PDF canvas (rendered when near the viewport,
// re-rendered on zoom) plus a transparent annotation canvas layered on top.
function PdfPage({
  pdf,
  blank,
  pageIndex,
  canEdit,
  tool,
  color,
  penSize,
  eraserSize,
  eraseMode,
  hiColor,
  hiSize,
  zoom,
  getStrokes,
  onCommitStroke,
  onEraseHit,
  onErasePartial,
  onEraseEnd,
  registerRedraw,
}) {
  const wrapRef = useRef(null);
  const pdfCanvasRef = useRef(null);
  const overlayRef = useRef(null);
  const renderTaskRef = useRef(null);
  const drawingRef = useRef(null); // in-progress pen/highlighter stroke
  const erasingRef = useRef(false);
  const eraserPosRef = useRef(null); // {px,py} device-px cursor for the eraser ring
  const mapRef = useRef(null); // { PW, PH, offX, offY } device-px page region
  const [visible, setVisible] = useState(false);
  const [rendered, setRendered] = useState(false);
  const [dims, setDims] = useState({ w: PAGE_RENDER_WIDTH, h: PAGE_RENDER_WIDTH * 1.414 });

  // Repaint the overlay from committed strokes plus any in-progress stroke.
  // Highlighter strokes are drawn first (underneath) so pen ink stays legible
  // on top of them. Finally, the live eraser ring is drawn over everything.
  const redraw = useCallback(() => {
    const canvas = overlayRef.current;
    const map = mapRef.current;
    if (!canvas || !map) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const committed = getStrokes(pageIndex);
    committed.forEach((s) => { if (s.tool === 'highlighter') drawStroke(ctx, s, map); });
    committed.forEach((s) => { if (s.tool !== 'highlighter') drawStroke(ctx, s, map); });
    if (drawingRef.current) drawStroke(ctx, drawingRef.current, map);

    if (tool === 'eraser' && eraserPosRef.current) {
      const { px, py } = eraserPosRef.current;
      const r = eraseRadiusPx(map, eraserSize);
      ctx.save();
      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(249,168,212,0.25)';
      ctx.fill();
      ctx.lineWidth = 1.5 * (map.PW / PAGE_RENDER_WIDTH);
      ctx.strokeStyle = 'rgba(17,24,39,0.6)';
      ctx.stroke();
      ctx.restore();
    }
  }, [getStrokes, pageIndex, tool, eraserSize]);

  // Let the parent trigger a repaint when a remote op arrives for this page.
  useEffect(() => {
    registerRedraw(pageIndex, redraw);
    return () => registerRedraw(pageIndex, null);
  }, [pageIndex, redraw, registerRedraw]);

  // Mark visible once the page scrolls near the viewport (then stop observing).
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '600px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Size the layers (and rasterize the PDF, if any) once visible; re-run on zoom
  // so both the PDF and the ink stay crisp. Blank boards skip rasterization —
  // the backdrop canvas just stays white (its bg-white class) — but still size
  // the overlay + coordinate map exactly the same way.
  useEffect(() => {
    if (!visible) return;
    if (!blank && !pdf) return; // pdf tab whose document hasn't opened yet
    let cancelled = false;
    (async () => {
      try {
        const dpr = window.devicePixelRatio || 1;
        const cssW = PAGE_RENDER_WIDTH * zoom;
        let deviceW;
        let deviceH;

        // Resolve the page aspect first so the resolution cap below can account
        // for the full mat (page + margins) — the overlay is the biggest canvas.
        let page;
        let base;
        let cssH;
        if (blank) {
          cssH = cssW * BLANK_ASPECT;
        } else {
          page = await pdf.getPage(pageIndex + 1); // pdf.js is 1-indexed
          if (cancelled) return;
          base = page.getViewport({ scale: 1 });
          cssH = cssW * (base.height / base.width);
        }
        // Scale device pixels down at high zoom so the overlay canvas stays under
        // Safari's max size (see MAX_CANVAS_* — otherwise it renders blank).
        const matCssW = cssW * (1 + 2 * MARGIN_X);
        const matCssH = cssH * (1 + 2 * MARGIN_Y);
        const effDpr = Math.min(
          dpr,
          MAX_CANVAS_DIM / matCssW,
          MAX_CANVAS_DIM / matCssH,
          Math.sqrt(MAX_CANVAS_AREA / (matCssW * matCssH))
        );

        if (blank) {
          deviceW = Math.round(cssW * effDpr);
          deviceH = Math.round(cssH * effDpr);
          setDims({ w: cssW, h: cssH });
        } else {
          const viewport = page.getViewport({ scale: (cssW / base.width) * effDpr });
          setDims({ w: cssW, h: cssH });

          const canvas = pdfCanvasRef.current;
          if (cancelled || !canvas) return; // unmounted mid-render
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          if (renderTaskRef.current) {
            try { renderTaskRef.current.cancel(); } catch (e) { /* ignore */ }
          }
          const task = page.render({ canvasContext: canvas.getContext('2d'), viewport });
          renderTaskRef.current = task;
          await task.promise;
          if (cancelled) return;
          deviceW = viewport.width;
          deviceH = viewport.height;
        }

        // The overlay is larger than the page by the margin on every side; record
        // the page region so pointer + draw math can map into it.
        const overlay = overlayRef.current;
        if (cancelled || !overlay) return;
        const offX = Math.round(MARGIN_X * deviceW);
        const offY = Math.round(MARGIN_Y * deviceH);
        mapRef.current = { PW: deviceW, PH: deviceH, offX, offY };
        overlay.width = deviceW + 2 * offX;
        overlay.height = deviceH + 2 * offY;
        setRendered(true);
        redraw();
      } catch (err) {
        // Swallow teardown races (tab switch destroys the pdf / unmounts the
        // page while an async render is still in flight); only log real failures.
        if (!cancelled && err?.name !== 'RenderingCancelledException') {
          console.error('Failed to render page', pageIndex, err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [visible, zoom, pdf, blank, pageIndex, redraw]);

  // Pointer position: device pixels on the overlay, plus normalized PAGE coords
  // (NOT clamped — values outside 0..1 are intentional, that's the margin).
  const readPointer = (e) => {
    const map = mapRef.current;
    const rect = overlayRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * overlayRef.current.width;
    const py = ((e.clientY - rect.top) / rect.height) * overlayRef.current.height;
    return { nx: (px - map.offX) / map.PW, ny: (py - map.offY) / map.PH, px, py };
  };

  const eraseAt = (e) => {
    const { px, py } = readPointer(e);
    const map = mapRef.current;
    const r = eraseRadiusPx(map, eraserSize);
    // Snapshot: the handlers mutate the live array as we iterate.
    [...getStrokes(pageIndex)].forEach((s) => {
      if (eraseMode === 'partial') {
        // Cut out only the touched section, keeping the surviving pieces.
        const survivors = splitStrokeByErase(s, px, py, map, r);
        if (survivors) onErasePartial(pageIndex, s, survivors);
      } else if (strokeHit(s, px, py, map, r)) {
        onEraseHit(pageIndex, s);
      }
    });
  };

  const handlePointerDown = (e) => {
    if (!canEdit || tool === 'pan') return;
    // Palm rejection: fingers/palm (pointerType 'touch') never draw — they are
    // reserved for scroll + pinch-zoom. Only a stylus ('pen') or mouse draws.
    // Returning BEFORE preventDefault lets the touch gesture reach the scroller.
    if (e.pointerType === 'touch') return;
    if (!mapRef.current || !overlayRef.current) return; // page not rendered yet
    e.preventDefault();
    overlayRef.current.setPointerCapture(e.pointerId);
    if (tool === 'eraser') {
      erasingRef.current = true;
      const { px, py } = readPointer(e);
      eraserPosRef.current = { px, py };
      eraseAt(e);
      redraw();
    } else {
      const { nx, ny } = readPointer(e);
      const isHi = tool === 'highlighter';
      drawingRef.current = {
        id: genId(),
        points: [[nx, ny]],
        color: isHi ? hiColor : color,
        width: isHi ? hiSize : penSize,
        tool: isHi ? 'highlighter' : 'pen',
      };
      redraw();
    }
  };

  const handlePointerMove = (e) => {
    if (!canEdit || tool === 'pan') return;
    if (e.pointerType === 'touch') return; // see palm-rejection note in handlePointerDown
    if (!mapRef.current || !overlayRef.current) return; // page not rendered yet
    if (tool === 'eraser') {
      // Track the cursor so the eraser ring follows even while just hovering.
      const { px, py } = readPointer(e);
      eraserPosRef.current = { px, py };
      if (erasingRef.current) eraseAt(e);
      redraw();
      return;
    }
    if (!drawingRef.current) return;
    const evts = e.getCoalescedEvents ? e.getCoalescedEvents() : [e];
    evts.forEach((ev) => {
      const { nx, ny } = readPointer(ev);
      drawingRef.current.points.push([nx, ny]);
    });
    redraw();
  };

  const handlePointerUp = () => {
    if (!canEdit) return;
    if (tool === 'eraser') {
      if (erasingRef.current) {
        erasingRef.current = false;
        onEraseEnd();
      }
      return;
    }
    if (!drawingRef.current) return;
    const stroke = drawingRef.current;
    drawingRef.current = null;
    if (stroke.points.length > 0) onCommitStroke(pageIndex, stroke);
    redraw();
  };

  // Hide the eraser ring when the pointer leaves the page (unless mid-drag,
  // where pointer capture keeps events flowing to this overlay).
  const handlePointerLeave = () => {
    if (tool === 'eraser' && !erasingRef.current) {
      eraserPosRef.current = null;
      redraw();
    }
  };

  const cursor =
    !canEdit || tool === 'pan'
      ? 'default'
      : tool === 'eraser'
      ? 'none' // the drawn ring is the eraser cursor
      : tool === 'highlighter'
      ? CURSOR_HI
      : CURSOR_PEN;

  // Page CSS size (at current zoom) and the surrounding drawable mat.
  const pageW = rendered ? dims.w : PAGE_RENDER_WIDTH * zoom;
  const pageH = rendered ? dims.h : PAGE_RENDER_WIDTH * zoom * 1.414;
  const matW = pageW * (1 + 2 * MARGIN_X);
  const matH = pageH * (1 + 2 * MARGIN_Y);

  return (
    <div
      ref={wrapRef}
      className="relative mx-auto mb-4"
      style={{
        width: matW,
        height: matH,
        // Show the edge of the drawable area (page + margins) so ink doesn't just
        // stop at an invisible boundary. Outline doesn't affect layout. Editors
        // only — students don't draw.
        outline: canEdit ? '1px dashed rgba(148,163,184,0.55)' : 'none',
        outlineOffset: '-1px',
      }}
    >
      {/* The PDF page sits inset within the mat; the margin around it is drawable. */}
      <canvas
        ref={pdfCanvasRef}
        className="absolute bg-white shadow-sm ring-1 ring-gray-200"
        style={{
          left: `${(MARGIN_X / (1 + 2 * MARGIN_X)) * 100}%`,
          top: `${(MARGIN_Y / (1 + 2 * MARGIN_Y)) * 100}%`,
          width: `${(1 / (1 + 2 * MARGIN_X)) * 100}%`,
          height: `${(1 / (1 + 2 * MARGIN_Y)) * 100}%`,
        }}
      />
      {!rendered && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-300 text-sm">
          Page {pageIndex + 1}
        </div>
      )}
      <canvas
        ref={overlayRef}
        className="absolute inset-0 w-full h-full"
        style={{
          // In pan mode the overlay ignores input so the container can scroll/pan.
          pointerEvents: tool === 'pan' ? 'none' : 'auto',
          // touch-action:none while a drawing tool is active is essential: it stops
          // the browser from ever hijacking a stylus stroke as a native scroll (which
          // would draw a dot then pan). Finger pan + pinch are handled in JS instead
          // (see the touch listeners on the scroll container).
          touchAction: tool === 'pan' ? 'auto' : 'none',
          // Belt-and-suspenders against the iOS "select all" blue highlight while
          // drawing/resting a palm.
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          cursor,
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerLeave}
      />
    </div>
  );
}

const DocumentWorkspace = ({ socket, classroomId, userId, userRole }) => {
  const canEdit = userRole === 'tutor' || userRole === 'admin';
  const [docs, setDocs] = useState([]); // all tabs: [{ id, kind, url, page_count, title }]
  const [activeDocId, setActiveDocId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, title } | null — drives the close-tab confirm modal
  const [deleting, setDeleting] = useState(false);
  const doc = useMemo(
    () => docs.find((d) => d.id === activeDocId) || null,
    [docs, activeDocId]
  );
  const [pdf, setPdf] = useState(null); // pdf.js document proxy (null for blank tabs)
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tool, setTool] = useState('pen'); // 'pen' | 'highlighter' | 'eraser' | 'pan'
  const [color, setColor] = useState(PEN_COLORS[0]); // pen colour
  const [penSize, setPenSize] = useState(3);
  const [hiColor, setHiColor] = useState(HI_COLORS[0]); // highlighter colour
  const [hiSize, setHiSize] = useState(6);
  const [eraserSize, setEraserSize] = useState(14);
  const [eraseMode, setEraseMode] = useState('whole'); // 'whole' | 'partial'
  const [zoom, setZoom] = useState(1);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const didFitRef = useRef(false); // one-time fit-to-width per document

  // Per-page committed strokes: pageIndex -> Stroke[]. A ref (not state) because
  // painting is driven imperatively via the registered redraw callbacks.
  const strokesRef = useRef(new Map());
  const redrawFns = useRef(new Map());

  // Per-tutor undo/redo. Each action is an array of { do, undo } op pairs applied
  // together (a pen stroke = 1 pair; an erase gesture = N pairs; clear-all snaps
  // every stroke so it can be restored). forceHist bumps to re-enable buttons.
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const eraseBuffer = useRef(null);
  const [, forceHist] = useReducer((x) => x + 1, 0);

  const getStrokes = useCallback((pageIndex) => {
    if (!strokesRef.current.has(pageIndex)) strokesRef.current.set(pageIndex, []);
    return strokesRef.current.get(pageIndex);
  }, []);

  const registerRedraw = useCallback((pageIndex, fn) => {
    if (fn) redrawFns.current.set(pageIndex, fn);
    else redrawFns.current.delete(pageIndex);
  }, []);

  const repaintPage = useCallback((pageIndex) => {
    const fn = redrawFns.current.get(pageIndex);
    if (fn) fn();
  }, []);

  // Apply an annotation op to local state (shared by local + remote ops).
  const applyOp = useCallback(
    (op) => {
      if (!op) return;
      if (op.type === 'stroke:add' && op.stroke) {
        const arr = getStrokes(op.pageIndex);
        if (!arr.some((s) => s.id === op.stroke.id)) arr.push(op.stroke);
        repaintPage(op.pageIndex);
      } else if (op.type === 'stroke:remove') {
        strokesRef.current.set(
          op.pageIndex,
          getStrokes(op.pageIndex).filter((s) => s.id !== op.strokeId)
        );
        repaintPage(op.pageIndex);
      } else if (op.type === 'clear') {
        strokesRef.current.set(op.pageIndex, []);
        repaintPage(op.pageIndex);
      } else if (op.type === 'clear-all') {
        strokesRef.current.forEach((_, p) => {
          strokesRef.current.set(p, []);
          repaintPage(p);
        });
      }
    },
    [getStrokes, repaintPage]
  );

  // Apply locally AND broadcast the delta. Used for every mutation (draw, erase,
  // clear, undo, redo) so all peers converge. No-ops on a null op (clear-all's
  // padding entries carry a null `do`/`undo`).
  const dispatch = useCallback(
    (op) => {
      if (!op) return;
      applyOp(op);
      if (doc) socket?.emit('annotation-op', { classroomId, documentId: doc.id, op });
    },
    [applyOp, socket, classroomId, doc]
  );

  const pushAction = useCallback((action) => {
    if (!action || action.length === 0) return;
    undoStack.current.push(action);
    redoStack.current = [];
    forceHist();
  }, []);

  const undo = useCallback(() => {
    const action = undoStack.current.pop();
    if (!action) return;
    [...action].reverse().forEach((pair) => dispatch(pair.undo));
    redoStack.current.push(action);
    forceHist();
  }, [dispatch]);

  const redo = useCallback(() => {
    const action = redoStack.current.pop();
    if (!action) return;
    action.forEach((pair) => dispatch(pair.do));
    undoStack.current.push(action);
    forceHist();
  }, [dispatch]);

  // Fetch the full tab list. Keep the current active tab if it still exists,
  // otherwise fall back to the first tab (or nothing).
  const loadDocumentList = useCallback(async () => {
    try {
      const resp = await fetch(apiUrl(`/api/documents/${classroomId}`));
      if (!resp.ok) throw new Error('Failed to load documents');
      const { documents } = await resp.json();
      const list = documents || [];
      setDocs(list);
      setActiveDocId((prev) =>
        prev && list.some((d) => d.id === prev) ? prev : list[0]?.id || null
      );
    } catch (err) {
      console.error(err);
      setDocs([]);
      setActiveDocId(null);
    } finally {
      setLoading(false);
    }
  }, [classroomId]);

  useEffect(() => {
    loadDocumentList();
  }, [loadDocumentList]);

  // Switch the active tab. The tutor drives the room: their switch is broadcast
  // so every student's view follows to the same document.
  const selectDoc = useCallback(
    (id) => {
      setActiveDocId(id);
      if (canEdit && socket) socket.emit('set-active-document', { classroomId, documentId: id });
    },
    [canEdit, socket, classroomId]
  );

  // Re-fetch when anyone changes the room's documents (e.g. the tutor uploads).
  // Also follow the tutor's active-tab broadcasts, and ask for the current one
  // on mount so a late-joining student lands on the right tab.
  useEffect(() => {
    if (!socket) return;
    const onDocsUpdated = ({ classroomId: cid }) => {
      if (cid === classroomId) loadDocumentList();
    };
    const onActiveDocument = ({ classroomId: cid, documentId }) => {
      if (cid === classroomId && documentId && !canEdit) setActiveDocId(documentId);
    };
    socket.on('documents-updated', onDocsUpdated);
    socket.on('active-document', onActiveDocument);
    if (!canEdit) socket.emit('active-document-request', { classroomId });
    return () => {
      socket.off('documents-updated', onDocsUpdated);
      socket.off('active-document', onActiveDocument);
    };
  }, [socket, classroomId, canEdit, loadDocumentList]);

  // Load the active tab: reset annotation + history state, then either open the
  // PDF (pdf tab) or just set a single white page (blank tab), and request the
  // tab's annotations. Keyed on the doc's identity fields (not the object) so a
  // change to the tab *list* doesn't needlessly reload the active document.
  useEffect(() => {
    if (!doc) {
      setPdf(null);
      setNumPages(0);
      return;
    }
    let cancelled = false;
    let task = null;
    strokesRef.current = new Map();
    undoStack.current = [];
    redoStack.current = [];
    eraseBuffer.current = null;
    forceHist();
    setPdf(null);

    if (doc.kind === 'blank' || !doc.url) {
      setNumPages(doc.page_count || 1);
    } else {
      setNumPages(0);
      task = pdfjsLib.getDocument(apiUrl(doc.url));
      task.promise
        .then((loaded) => {
          if (cancelled) return;
          setPdf(loaded);
          setNumPages(loaded.numPages);
        })
        .catch((err) => {
          if (!cancelled) {
            console.error('Failed to open PDF:', err);
            toast.error('Could not open document');
          }
        });
    }
    if (socket) socket.emit('annotations-request', { documentId: doc.id });
    return () => {
      cancelled = true;
      task?.destroy?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, doc?.url, doc?.kind, socket]);

  // Keep a blank board's page count in sync when the tutor adds a page, WITHOUT
  // going through the reload effect above (which would reset ink + undo). Growing
  // numPages simply mounts another blank page; existing annotations are untouched.
  useEffect(() => {
    if (doc?.kind === 'blank') setNumPages(doc.page_count || 1);
  }, [doc?.kind, doc?.page_count]);

  // Once per active tab, fit the page + margins to the panel width. Runs for
  // blank tabs immediately and for pdf tabs once the document has loaded.
  useEffect(() => {
    didFitRef.current = false;
  }, [activeDocId]);
  useEffect(() => {
    if (didFitRef.current || !scrollRef.current || !doc) return;
    if (doc.kind !== 'blank' && !pdf) return; // wait for the PDF to open first
    const avail = scrollRef.current.clientWidth - 32; // account for p-4 padding
    const matBaseW = PAGE_RENDER_WIDTH * (1 + 2 * MARGIN_X);
    setZoom(clampZoom(avail / matBaseW));
    didFitRef.current = true;
  }, [pdf, doc, activeDocId]);

  // Incoming annotation sync.
  useEffect(() => {
    if (!socket) return;
    const onOp = ({ documentId, op }) => {
      if (doc && documentId === doc.id) applyOp(op);
    };
    const onLoad = ({ documentId, pages }) => {
      if (!doc || documentId !== doc.id) return;
      strokesRef.current = new Map();
      Object.entries(pages || {}).forEach(([pageIndex, strokes]) => {
        strokesRef.current.set(Number(pageIndex), strokes || []);
        repaintPage(Number(pageIndex));
      });
    };
    socket.on('annotation-op', onOp);
    socket.on('annotations-load', onLoad);
    return () => {
      socket.off('annotation-op', onOp);
      socket.off('annotations-load', onLoad);
    };
  }, [socket, doc, applyOp, repaintPage]);

  // Keyboard undo/redo for the tutor.
  useEffect(() => {
    if (!canEdit) return;
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.key === 'z' || e.key === 'Z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (e.key === 'y' || e.key === 'Y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canEdit, undo, redo]);

  // Commit a locally drawn stroke: apply + broadcast + record for undo.
  const handleCommitStroke = useCallback(
    (pageIndex, stroke) => {
      const full = { ...stroke, authorId: userId, tool: stroke.tool || 'pen' };
      const addOp = { type: 'stroke:add', pageIndex, stroke: full };
      dispatch(addOp);
      pushAction([{ do: addOp, undo: { type: 'stroke:remove', pageIndex, strokeId: full.id } }]);
    },
    [dispatch, pushAction, userId]
  );

  // Eraser: remove each hit stroke immediately; group the whole drag into one
  // undo action so a single undo restores everything erased in that gesture.
  const handleEraseHit = useCallback(
    (pageIndex, stroke) => {
      const removeOp = { type: 'stroke:remove', pageIndex, strokeId: stroke.id };
      dispatch(removeOp);
      if (!eraseBuffer.current) eraseBuffer.current = [];
      eraseBuffer.current.push({ do: removeOp, undo: { type: 'stroke:add', pageIndex, stroke } });
    },
    [dispatch]
  );

  // Partial eraser: replace the touched stroke with its surviving pieces. This
  // is a remove-original + add-each-piece group, recorded (like whole-erase) into
  // the current gesture so one undo restores everything cut in that drag.
  const handleErasePartial = useCallback(
    (pageIndex, stroke, survivors) => {
      if (!eraseBuffer.current) eraseBuffer.current = [];
      const removePair = {
        do: { type: 'stroke:remove', pageIndex, strokeId: stroke.id },
        undo: { type: 'stroke:add', pageIndex, stroke },
      };
      dispatch(removePair.do);
      eraseBuffer.current.push(removePair);
      survivors.forEach((sub) => {
        const addPair = {
          do: { type: 'stroke:add', pageIndex, stroke: sub },
          undo: { type: 'stroke:remove', pageIndex, strokeId: sub.id },
        };
        dispatch(addPair.do);
        eraseBuffer.current.push(addPair);
      });
    },
    [dispatch]
  );

  const handleEraseEnd = useCallback(() => {
    if (eraseBuffer.current) pushAction(eraseBuffer.current);
    eraseBuffer.current = null;
  }, [pushAction]);

  const handleClearAll = () => {
    if (!doc) return;
    const clearOp = { type: 'clear-all' };
    // Snapshot every stroke first so clear can be undone.
    const entries = [{ do: clearOp, undo: null }];
    strokesRef.current.forEach((arr, p) => {
      arr.forEach((s) => entries.push({ do: null, undo: { type: 'stroke:add', pageIndex: p, stroke: s } }));
    });
    dispatch(clearOp);
    pushAction(entries);
    toast.success('Annotations cleared');
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    setUploading(true);
    const t = toast.loading('Uploading document...');
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      formData.append('classroomId', classroomId);
      formData.append('userId', userId);
      const resp = await fetch(apiUrl('/api/documents/upload'), { method: 'POST', body: formData });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || body.message || 'Upload failed');
      toast.success('Document uploaded', { id: t });
      if (body.document?.id) selectDoc(body.document.id); // open the new tab
      loadDocumentList();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.message || 'Upload failed', { id: t });
    } finally {
      setUploading(false);
    }
  };

  // Add a blank whiteboard tab and switch to it.
  const handleAddBlank = async () => {
    setUploading(true);
    try {
      const resp = await fetch(apiUrl('/api/documents/blank'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ classroomId, userId }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || 'Failed to add board');
      if (body.document?.id) selectDoc(body.document.id);
      loadDocumentList();
    } catch (err) {
      console.error('Add board error:', err);
      toast.error(err.message || 'Failed to add board');
    } finally {
      setUploading(false);
    }
  };

  // Add a page to the current blank board (shared across the room).
  const handleAddPage = async () => {
    if (!doc || doc.kind !== 'blank') return;
    try {
      const resp = await fetch(apiUrl(`/api/documents/${doc.id}/add-page`), { method: 'POST' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || 'Failed to add page');
      loadDocumentList(); // refreshes page_count locally; broadcast covers others
    } catch (err) {
      console.error('Add page error:', err);
      toast.error(err.message || 'Failed to add page');
    }
  };

  // Delete a tab (PDF file + annotations cascade server-side). Gated by the
  // in-app confirm modal (setPendingDelete), so no native window.confirm.
  const handleDeleteDoc = async (id) => {
    setDeleting(true);
    try {
      const resp = await fetch(apiUrl(`/api/documents/${id}`), { method: 'DELETE' });
      if (!resp.ok) throw new Error('Failed to delete');
      loadDocumentList(); // documents-updated will also refresh other clients
      setPendingDelete(null);
    } catch (err) {
      console.error('Delete document error:', err);
      toast.error('Could not close tab');
    } finally {
      setDeleting(false);
    }
  };

  // Container-level panning (click-drag to scroll) when the pan tool is active.
  // The overlay drops pointerEvents in pan mode, so these fire for the whole
  // page area; capturing the pointer keeps the drag alive even as it passes over
  // child canvases. Touch/trackpad also scroll natively.
  const panRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const handleContainerPointerDown = (e) => {
    if (tool !== 'pan' || !scrollRef.current) return;
    if (e.pointerType === 'touch') return; // touch pans via native scroll (touch-action:auto)
    panRef.current = {
      x: e.clientX,
      y: e.clientY,
      left: scrollRef.current.scrollLeft,
      top: scrollRef.current.scrollTop,
    };
    setIsPanning(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
  };
  const handleContainerPointerMove = (e) => {
    if (!panRef.current || !scrollRef.current) return;
    scrollRef.current.scrollLeft = panRef.current.left - (e.clientX - panRef.current.x);
    scrollRef.current.scrollTop = panRef.current.top - (e.clientY - panRef.current.y);
  };
  const endPan = () => {
    if (panRef.current) setIsPanning(false);
    panRef.current = null;
  };

  // Ctrl/Cmd + wheel to zoom. Registered as a NON-passive native listener —
  // React's synthetic onWheel is passive, so calling preventDefault there is
  // ignored and logs "Unable to preventDefault inside passive event listener".
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return; // plain scroll falls through natively
      e.preventDefault();
      setZoom((z) => clampZoom(z + (e.deltaY < 0 ? 0.1 : -0.1)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Refs so the once-registered touch listeners below read fresh values without
  // re-binding mid-gesture.
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  const toolRef = useRef(tool);
  useEffect(() => { toolRef.current = tool; }, [tool]);

  // Custom one-finger pan + two-finger pinch-zoom for touch. The drawing overlay
  // is touch-action:none (so the stylus is never stolen by native scrolling), so
  // finger gestures are driven here instead. Rules:
  //   • If a stylus is touching, fingers are treated as palm and ignored — this
  //     is the palm rejection: you can rest your hand while drawing.
  //   • No stylus + 1 finger → pan; + 2 fingers → pinch-zoom.
  // Pinch is focal-anchored: the content point under the pinch midpoint stays
  // put (fixes zoom-to-top-left). The scroll adjustment lands in the layout
  // effect below, after React has resized the pages for the new zoom.
  const gestureRef = useRef({ mode: null });
  const pinchApplyRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const fingers = (e) => [...e.touches].filter((t) => t.touchType !== 'stylus');
    const hasStylus = (e) => [...e.touches].some((t) => t.touchType === 'stylus');
    const dist = (f) => Math.hypot(f[0].clientX - f[1].clientX, f[0].clientY - f[1].clientY);
    const mid = (f) => ({
      x: (f[0].clientX + f[1].clientX) / 2,
      y: (f[0].clientY + f[1].clientY) / 2,
    });
    const onTouchStart = (e) => {
      if (toolRef.current === 'pan') return; // pan tool scrolls natively
      // Own every touch while a drawing tool is active. This is what stops iOS
      // from (a) using the stylus/palm touch to start a native scroll — which
      // fires pointercancel and drops the pen stroke (the "every 2nd/3rd stroke
      // doesn't draw" bug) — and (b) triggering text selection / select-all.
      // The pen still draws: it comes through the overlay's POINTER events, which
      // preventDefault on touch events does not suppress.
      e.preventDefault();
      if (hasStylus(e)) { gestureRef.current = { mode: null }; return; }
      const f = fingers(e);
      const rect = el.getBoundingClientRect();
      if (f.length === 2) {
        const m = mid(f);
        gestureRef.current = {
          mode: 'pinch',
          startDist: dist(f) || 1,
          startZoom: zoomRef.current,
          fracX: (el.scrollLeft + (m.x - rect.left)) / (el.scrollWidth || 1),
          fracY: (el.scrollTop + (m.y - rect.top)) / (el.scrollHeight || 1),
        };
      } else if (f.length === 1) {
        gestureRef.current = {
          mode: 'pan',
          startX: f[0].clientX,
          startY: f[0].clientY,
          scrollLeft: el.scrollLeft,
          scrollTop: el.scrollTop,
        };
      }
    };
    const onTouchMove = (e) => {
      if (toolRef.current === 'pan') return;
      e.preventDefault(); // keep owning the gesture (stops scroll-steal + selection)
      if (hasStylus(e)) return; // stylus draws via pointer events; ignore here
      const g = gestureRef.current;
      const f = fingers(e);
      if (g.mode === 'pinch' && f.length === 2) {
        e.preventDefault();
        const z1 = clampZoom(g.startZoom * (dist(f) / g.startDist));
        const m = mid(f);
        pinchApplyRef.current = { fracX: g.fracX, fracY: g.fracY, midX: m.x, midY: m.y };
        setZoom(z1);
      } else if (g.mode === 'pan' && f.length === 1) {
        e.preventDefault();
        el.scrollLeft = g.scrollLeft - (f[0].clientX - g.startX);
        el.scrollTop = g.scrollTop - (f[0].clientY - g.startY);
      }
    };
    const onTouchEnd = (e) => {
      const f = fingers(e);
      if (f.length === 0) {
        gestureRef.current = { mode: null };
        pinchApplyRef.current = null;
      } else if (f.length === 1) {
        // Lifting one finger of a pinch → continue as a pan with the survivor.
        gestureRef.current = {
          mode: 'pan',
          startX: f[0].clientX,
          startY: f[0].clientY,
          scrollLeft: el.scrollLeft,
          scrollTop: el.scrollTop,
        };
      }
    };
    const suppressGesture = (e) => e.preventDefault(); // iOS Safari viewport zoom
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    el.addEventListener('gesturestart', suppressGesture, { passive: false });
    el.addEventListener('gesturechange', suppressGesture, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('gesturestart', suppressGesture);
      el.removeEventListener('gesturechange', suppressGesture);
    };
  }, []);

  // After a pinch changes `zoom` (and the pages re-render at the new size), keep
  // the pinched content point under the finger midpoint.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const a = pinchApplyRef.current;
    if (!el || !a) return;
    const rect = el.getBoundingClientRect();
    el.scrollLeft = a.fracX * el.scrollWidth - (a.midX - rect.left);
    el.scrollTop = a.fracY * el.scrollHeight - (a.midY - rect.top);
  }, [zoom]);

  const toolBtn = (id, label, title) => (
    <button
      onClick={() => setTool(id)}
      title={title}
      className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
        tool === id ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
      }`}
    >
      {label}
    </button>
  );

  // A round colour swatch button for the pen / highlighter palettes.
  const swatch = (c, active, onClick, title) => (
    <button
      key={c}
      onClick={onClick}
      title={title}
      className={`w-6 h-6 rounded-full transition-transform ${
        active ? 'ring-2 ring-offset-1 ring-primary-500 scale-110' : 'ring-1 ring-gray-300 hover:scale-105'
      }`}
      style={{ backgroundColor: c }}
    />
  );

  // Size slider config for whichever tool is active (null for pan).
  const sizeCfg =
    tool === 'eraser'
      ? { value: eraserSize, set: setEraserSize, min: 6, max: 48, label: 'Eraser size' }
      : tool === 'highlighter'
      ? { value: hiSize, set: setHiSize, min: 3, max: 16, label: 'Highlighter size' }
      : tool === 'pen'
      ? { value: penSize, set: setPenSize, min: 1, max: 10, label: 'Pen size' }
      : null;

  const pageIndexes = Array.from({ length: numPages }, (_, i) => i);
  const canUndo = undoStack.current.length > 0;
  const canRedo = redoStack.current.length > 0;

  return (
    <div className="h-full w-full flex flex-col bg-gray-100 overflow-hidden">
      {/* Custom scrollbar: blue track, brand-yellow thumb. Always show a track so
          it's an obvious affordance that the document scrolls. */}
      <style>{`
        .doc-scroll { scrollbar-color: #F4C70F #243a7a; scrollbar-width: auto; }
        .doc-scroll::-webkit-scrollbar { width: 14px; height: 14px; }
        .doc-scroll::-webkit-scrollbar-track { background: #243a7a; }
        .doc-scroll::-webkit-scrollbar-thumb {
          background: #F4C70F; border-radius: 8px; border: 3px solid #243a7a;
        }
        .doc-scroll::-webkit-scrollbar-thumb:hover { background: #D4A800; }
        .doc-scroll::-webkit-scrollbar-corner { background: #243a7a; }
      `}</style>

      {/* Toolbar */}
      <div className="border-b border-gray-200 px-3 py-1.5 flex items-center justify-between bg-gray-50 flex-shrink-0 z-10 gap-2">
        <div className="flex items-center min-w-0">
          <div className={`w-2 h-2 rounded-full mr-1.5 flex-shrink-0 ${canEdit ? 'bg-green-500' : 'bg-blue-500'}`} />
          <span className="text-xs text-gray-600 truncate max-w-[160px]">
            {doc ? doc.title || 'Document' : 'Whiteboard'}
            {!canEdit && ' (View Only)'}
          </span>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
          {/* Zoom — available to everyone */}
          {pdf && (
            <div className="flex items-center gap-1 mr-1">
              <button
                onClick={() => setZoom((z) => clampZoom(z - 0.25))}
                className="w-6 h-6 flex items-center justify-center text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                title="Zoom out"
              >−</button>
              <span className="text-xs text-gray-600 w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
              <button
                onClick={() => setZoom((z) => clampZoom(z + 0.25))}
                className="w-6 h-6 flex items-center justify-center text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                title="Zoom in"
              >+</button>
            </div>
          )}

          {canEdit && doc && (
            <>
              {toolBtn('pen', 'Pen', 'Draw (P)')}
              {toolBtn('highlighter', 'Highlighter', 'Highlight')}
              {toolBtn('eraser', 'Eraser', 'Erase strokes')}
              {toolBtn('pan', 'Pan', 'Pan / scroll')}

              {/* Pen colour palette */}
              {tool === 'pen' && (
                <div className="flex items-center gap-1 pl-1 border-l border-gray-300 ml-0.5">
                  {PEN_COLORS.map((c) =>
                    swatch(c, color === c, () => setColor(c), 'Pen colour')
                  )}
                </div>
              )}

              {/* Highlighter colour palette */}
              {tool === 'highlighter' && (
                <div className="flex items-center gap-1 pl-1 border-l border-gray-300 ml-0.5">
                  {HI_COLORS.map((c) =>
                    swatch(c, hiColor === c, () => setHiColor(c), 'Highlighter colour')
                  )}
                </div>
              )}

              {/* Eraser mode — Whole removes an entire stroke, Partial rubs out
                  only the bit under the cursor (like a real eraser). */}
              {tool === 'eraser' && (
                <div className="flex items-center rounded overflow-hidden border border-gray-300 ml-0.5">
                  {[
                    ['whole', 'Whole'],
                    ['partial', 'Partial'],
                  ].map(([mode, label]) => (
                    <button
                      key={mode}
                      onClick={() => setEraseMode(mode)}
                      title={mode === 'whole' ? 'Erase whole strokes' : 'Erase only where you touch'}
                      className={`px-2 py-1 text-xs font-medium transition-colors ${
                        eraseMode === mode ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {/* Size scale for the active tool — slider for quick drags, plus a
                  numeric box for an exact thickness. Both clamp to [min, max]. */}
              {sizeCfg && (
                <div className="flex items-center gap-1.5 pl-1.5 border-l border-gray-300 ml-0.5">
                  <input
                    type="range"
                    min={sizeCfg.min}
                    max={sizeCfg.max}
                    value={sizeCfg.value}
                    onChange={(e) => sizeCfg.set(Number(e.target.value))}
                    title={sizeCfg.label}
                    aria-label={sizeCfg.label}
                    className="w-20 accent-primary-600 cursor-pointer"
                  />
                  <input
                    type="number"
                    min={sizeCfg.min}
                    max={sizeCfg.max}
                    value={sizeCfg.value}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      if (Number.isNaN(n)) return;
                      sizeCfg.set(Math.min(sizeCfg.max, Math.max(sizeCfg.min, n)));
                    }}
                    title={sizeCfg.label}
                    aria-label={`${sizeCfg.label} (exact)`}
                    className="w-12 px-1 py-0.5 text-xs text-center border border-gray-300 rounded tabular-nums"
                  />
                  <span className="text-xs text-gray-400">px</span>
                </div>
              )}

              <button
                onClick={undo}
                disabled={!canUndo}
                title="Undo (Ctrl/Cmd+Z)"
                className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >↶</button>
              <button
                onClick={redo}
                disabled={!canRedo}
                title="Redo (Ctrl/Cmd+Shift+Z)"
                className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >↷</button>
            </>
          )}

          {canEdit && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={(e) => { handleUpload(e.target.files[0]); e.target.value = ''; }}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-2.5 py-1 bg-primary-600 text-white text-xs font-medium rounded hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Working…' : '+ PDF'}
              </button>
              <button
                onClick={handleAddBlank}
                disabled={uploading}
                className="px-2.5 py-1 bg-white text-gray-700 border border-gray-300 text-xs font-medium rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                + Board
              </button>
              {doc && (
                <button
                  onClick={handleClearAll}
                  className="px-2.5 py-1 text-white text-xs font-medium rounded transition-colors"
                  style={{ backgroundColor: '#F4C70F' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#D4A800')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#F4C70F')}
                >
                  Clear
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Tab bar — one tab per open document. The tutor manages tabs and their
          switch drives the room; students see the same tabs and follow along. */}
      {docs.length > 0 && (
        <div className="flex items-stretch gap-1 px-2 pt-1 bg-gray-100 border-b border-gray-200 overflow-x-auto flex-shrink-0">
          {docs.map((d) => {
            const active = d.id === activeDocId;
            return (
              <div
                key={d.id}
                onClick={() => selectDoc(d.id)}
                title={d.title}
                className={`group flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-t text-xs cursor-pointer whitespace-nowrap border-b-2 transition-colors ${
                  active
                    ? 'bg-white border-primary-600 text-gray-900'
                    : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-200'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    d.kind === 'blank' ? 'bg-gray-400' : 'bg-primary-500'
                  }`}
                />
                <span className="max-w-[140px] truncate">
                  {d.title || (d.kind === 'blank' ? 'Board' : 'Document')}
                </span>
                {canEdit && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setPendingDelete({ id: d.id, title: d.title }); }}
                    title="Close tab"
                    className="ml-0.5 w-4 h-4 flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-gray-200 leading-none"
                  >×</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Scroll / pan area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-auto p-4 doc-scroll"
        style={{
          minHeight: 0,
          cursor: tool === 'pan' ? (isPanning ? 'grabbing' : 'grab') : 'default',
          // Prevent the iOS select-all highlight when a palm/finger lands on the
          // workspace background while drawing.
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
        onPointerDown={handleContainerPointerDown}
        onPointerMove={handleContainerPointerMove}
        onPointerUp={endPan}
        onPointerLeave={endPan}
      >
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading...</p>
            </div>
          </div>
        ) : !doc ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-gray-500">
              <p className="mb-1 font-medium">No document open</p>
              <p className="text-sm">
                {canEdit ? 'Add a PDF or a blank board to start.' : 'Waiting for the tutor to open a document.'}
              </p>
            </div>
          </div>
        ) : doc.kind !== 'blank' && !pdf ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
          </div>
        ) : (
          <div style={{ width: 'fit-content', minWidth: '100%', margin: '0 auto' }}>
            {pageIndexes.map((i) => (
              <PdfPage
                key={`${doc.id}-${i}`}
                pdf={pdf}
                blank={doc.kind === 'blank'}
                pageIndex={i}
                canEdit={canEdit}
                tool={tool}
                color={color}
                penSize={penSize}
                eraserSize={eraserSize}
                eraseMode={eraseMode}
                hiColor={hiColor}
                hiSize={hiSize}
                zoom={zoom}
                getStrokes={getStrokes}
                onCommitStroke={handleCommitStroke}
                onEraseHit={handleEraseHit}
                onErasePartial={handleErasePartial}
                onEraseEnd={handleEraseEnd}
                registerRedraw={registerRedraw}
              />
            ))}

            {/* Add another page to a blank board (tutor only). */}
            {canEdit && doc.kind === 'blank' && (
              <button
                onClick={handleAddPage}
                className="mb-4 w-full py-3 rounded border-2 border-dashed border-gray-300 text-gray-500 text-sm font-medium hover:border-primary-400 hover:text-primary-600 hover:bg-white/60 transition-colors"
              >
                + Add page
              </button>
            )}
          </div>
        )}
      </div>

      {/* Close-tab confirmation — replaces the native window.confirm so the
          "deletes for everyone" warning stays on-brand. */}
      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => { if (!deleting) setPendingDelete(null); }}
        >
          <div
            className="w-full max-w-sm rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="p-5">
              <h3 className="text-base font-semibold text-gray-900">
                Close “{pendingDelete.title || 'this tab'}”?
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                This deletes the tab and its annotations for <span className="font-medium text-gray-700">everyone</span> in the classroom, and can’t be undone.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
              <button
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 rounded hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteDoc(pendingDelete.id)}
                disabled={deleting}
                className="px-3 py-1.5 text-sm font-medium text-white rounded bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Closing…' : 'Close tab'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentWorkspace;
